import { isFiniteNumber } from '@core';

const getPositionNumber = (position: string) => {
    const positionNumber = Number(position.slice(1));
    return Number.isNaN(positionNumber) ? null : positionNumber;
};

const getAvailableSegmentationEntries = (segmentationMap: Record<string, number> | undefined) => {
    return Object.entries(segmentationMap ?? {})
        .filter(([, frame]) => frame != null)
        .sort(([positionA], [positionB]) => {
            const positionNumberA = getPositionNumber(positionA) ?? Number.POSITIVE_INFINITY;
            const positionNumberB = getPositionNumber(positionB) ?? Number.POSITIVE_INFINITY;
            return positionNumberA - positionNumberB;
        });
};

/**
 * Resolve a target frame using only the target swing's own segmentation data.
 *
 * If the requested P-position is missing, the nearest available P-position in the target swing is used.
 * If there is no segmentation data, return null so the caller can fall back to the normal reset behavior.
 */
export const resolveTargetFrameFromPosition = (
    targetPosition: string,
    targetSegmentationMap: Record<string, number> | undefined,
    targetTotalFrames: number,
) => {
    const availableEntries = getAvailableSegmentationEntries(targetSegmentationMap);

    if (!availableEntries.length) {
        return null;
    }

    const directFrame = targetSegmentationMap?.[targetPosition];
    if (isFiniteNumber(directFrame)) {
        return Math.min(directFrame, targetTotalFrames);
    }

    const requestedPositionNumber = getPositionNumber(targetPosition);
    if (!isFiniteNumber(requestedPositionNumber)) {
        return Math.min(availableEntries[0][1], targetTotalFrames);
    }

    const nearestEntry = availableEntries.reduce(
        (closest, entry) => {
            const [position, frame] = entry;
            const positionNumber = getPositionNumber(position);
            if (positionNumber == null) {
                return closest;
            }

            const distance = Math.abs(positionNumber - requestedPositionNumber);
            if (distance < closest.distance) {
                return { distance, frame };
            }

            return closest;
        },
        { distance: Number.POSITIVE_INFINITY, frame: availableEntries[0][1] },
    );

    return Math.min(nearestEntry.frame, targetTotalFrames);
};
